/**
 * Decodes StreamDataMessage-style envelope payload when it contains base64-encoded value.
 * Wire format: payload.type_url + payload.value (base64). Decodes value and attaches
 * a usable decodedPayload for consumer components (priceFeed, indexPrice, etc.).
 */

import { normalizeSnakeToCamel } from "./normalize";

/** Base64 decode (browser-safe). */
function base64DecodeToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Try to interpret bytes as UTF-8 and parse as JSON. Returns null if not valid JSON. */
function tryDecodeAsJson(bytes: Uint8Array): unknown {
  try {
    const text = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    const parsed = JSON.parse(text);
    return parsed;
  } catch {
    return null;
  }
}

export interface DecodedStreamEnvelope {
  /** Original envelope (version, message_type, timestamp, first_sequence_id, last_sequence_id). */
  version?: string;
  messageType?: string;
  timestamp?: { seconds?: number; nanos?: number };
  firstSequenceId?: number;
  lastSequenceId?: number;
  /** Decoded payload body (when payload.value was base64). CamelCase if from JSON. */
  decodedPayload?: unknown;
  /** Original payload.type_url for reference. */
  typeUrl?: string;
  /** When value could not be decoded to JSON (binary proto), decoded bytes. */
  decodedPayloadRaw?: Uint8Array;
}

/**
 * If data has payload.value as base64 string, decodes it and returns envelope with
 * decodedPayload (and optionally decodedPayloadRaw for binary). Otherwise returns data unchanged.
 * Design: backend may send google.protobuf.Any with value = base64(JSON) or base64(proto bytes).
 * We decode base64 and best-effort parse as JSON; otherwise expose raw bytes.
 */
export function decodeStreamPayload(data: unknown): unknown {
  if (data == null || typeof data !== "object") return data;
  const obj = data as Record<string, unknown>;
  const payload = obj.payload as Record<string, unknown> | undefined;
  const value = payload?.value;
  if (typeof value !== "string") return data;

  let decodedPayload: unknown = undefined;
  let decodedPayloadRaw: Uint8Array | undefined = undefined;
  try {
    const bytes = base64DecodeToBytes(value);
    decodedPayloadRaw = bytes;
    const asJson = tryDecodeAsJson(bytes);
    if (asJson !== null && typeof asJson === "object") {
      decodedPayload = normalizeSnakeToCamel(asJson);
    }
  } catch (e) {
    console.warn("[streamPayload] decode failed:", e);
  }

  const envelope: DecodedStreamEnvelope & Record<string, unknown> = {
    ...obj,
    version: (obj.version as string) ?? undefined,
    messageType: (obj.message_type ?? obj.messageType) as string | undefined,
    timestamp: (obj.timestamp as { seconds?: number; nanos?: number }) ?? undefined,
    firstSequenceId: (obj.first_sequence_id ?? obj.firstSequenceId) as number | undefined,
    lastSequenceId: (obj.last_sequence_id ?? obj.lastSequenceId) as number | undefined,
    typeUrl: (payload?.type_url ?? payload?.typeUrl) as string | undefined,
  };
  if (decodedPayload !== undefined) envelope.decodedPayload = decodedPayload;
  if (decodedPayloadRaw !== undefined)
    envelope.decodedPayloadRaw = decodedPayloadRaw;
  return envelope;
}
